/**
 * CTO Email report (task 035-03) — "Common CTO Leads": one row per traffic
 * source (site) with her columns, a click-in to the source's links with the
 * same columns per link, totals that recompute *Unique* and *New to File*,
 * sortable headers, and the three export buttons per row.
 *
 * Level 1 = `data.sources[]` (README decision 6): the traffic-source label
 * cell is a button that drills into level 2 = `data.links[]` filtered to
 * that source. Level 2's totals row is the SELECTED SOURCE's own row from
 * `sources[]` — never the sum of the link rows (a source's unique is
 * deliberately less than its links' sum). Rate / cost-of-unique-leads follow
 * the mixed / partly-unpriced rule from the pinned contract
 * (`task_02_cto_stats_query_opus.md` §Contract): exactly one rate and no
 * unpriced link ⇒ `rate × source unique`; otherwise the per-link sum with the
 * mixed marker (an unpriced link contributes 0, never a sibling's rate).
 *
 * Talks to the api through the screen-local `cto-stats-api.ts` (A2 (b)) and
 * the shared `api` client for the campaign dropdown only (007's precedent).
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type CampaignDetailResponse } from '../../lib/api';
import { fmtMoney } from '../../lib/format';
import { Card } from '../../components/ui/card';
import { Select } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  fetchCtoStats,
  ctoLeadsExportUrl,
  type CtoLeadsVariant,
  type CtoLinkRow,
  type CtoSourceRow,
  type CtoStatsResponse,
} from './cto-stats-api';
import { ReportShell } from './report-shell';
import {
  RangeFilters,
  ReportSearch,
  SearchNote,
  TestLeadsToggle,
  matchesSearch,
  useReportRange,
  useSharedReportFilters,
} from './report-filters';
import {
  cell,
  EmptyRow,
  ErrorBanner,
  LoadingRow,
  SortableHeader,
  UNIQUE_TOTAL_TITLE,
  useClientSort,
} from './report-table';

/** The "marked" mixed / partly-unpriced cost cell (task 03 §Scope 2, verbatim). */
const MIXED_COST_TITLE =
  'Mixed or partly unpriced rates — sum of per-link costs; an unpriced link contributes 0 and an address on two links may count twice';

/** Level-1 totals row's `Cost of unique leads` title — that cell IS the sum of the rows above it. */
const LEVEL1_TOTALS_COST_TITLE = 'Adds up the source rows above';

/**
 * Level-2 (link grain) `Cost of unique leads` title, on both the column
 * header and the totals row: the contrast that is on screen — the totals row
 * is the selected source's own cost, not the sum of the link rows below it.
 * The task file does not quote a literal for this one (unlike level-1's,
 * which is quoted); wording chosen to state the same contrast in her
 * language — logged under Decisions made.
 */
const LEVEL2_COST_TITLE =
  "This source's cost across its links — not the sum of the link rows below";

const UNIQUE_COLUMN_HELP = "Distinct addresses across this source's links in the range";
const NOT_CHECKED_COLUMN_HELP =
  "Beehiiv 'The Smart Investor' lookup — new-to-file status, distinct addresses";

/** `NN.NN %`, empty when `null` (uniqueLeads = 0 — the contract's rule). */
function fmtNewToFile(pct: number | null): string {
  if (pct === null) return '';
  return `${pct.toFixed(2)} %`;
}

interface RateCell {
  text: string;
  title?: string;
}

/**
 * The Rate cell rule (task 03 §Scope 2): one rate ⇒ that value through
 * `fmtMoney`; several rates or a partly-unpriced source ⇒ `mixed` with the
 * rates list + "unpriced link(s)" as the title; every link unpriced
 * (`rates` empty) ⇒ empty text with `title="unpriced link(s)"`.
 */
function rateCell(rates: number[], hasUnpricedLink: boolean, mixed: boolean): RateCell {
  if (rates.length === 0) return { text: '', title: 'unpriced link(s)' };
  if (mixed) {
    const parts = rates.map((r) => fmtMoney(r));
    if (hasUnpricedLink) parts.push('unpriced link(s)');
    return { text: 'mixed', title: parts.join(', ') };
  }
  return { text: fmtMoney(rates[0]) };
}

type SourceSortKey =
  | 'siteCode'
  | 'label'
  | 'leads'
  | 'uniqueLeads'
  | 'success'
  | 'duplicate'
  | 'unchecked'
  | 'rejected'
  | 'newToFilePct'
  | 'rate'
  | 'costOfTotal'
  | 'costOfUnique';

/** The Rate sort proxy: the single rate when unambiguous, else `null` (sorts last — mixed/unpriced). */
function sourceAccessor(row: CtoSourceRow, key: SourceSortKey): string | number | null {
  switch (key) {
    case 'siteCode':
      return row.siteCode;
    case 'label':
      return row.label;
    case 'leads':
      return row.leads;
    case 'uniqueLeads':
      return row.uniqueLeads;
    case 'success':
      return row.success;
    case 'duplicate':
      return row.duplicate;
    case 'unchecked':
      return row.unchecked;
    case 'rejected':
      return row.rejected;
    case 'newToFilePct':
      return row.newToFilePct;
    case 'rate':
      return row.rates.length === 1 && !row.costOfUniqueMixed ? row.rates[0] : null;
    case 'costOfTotal':
      return row.costOfTotal;
    case 'costOfUnique':
      return row.costOfUnique;
  }
}

type LinkSortKey =
  | 'campaignName'
  | 'promoTypeName'
  | 'linkStatus'
  | 'leads'
  | 'uniqueLeads'
  | 'success'
  | 'duplicate'
  | 'unchecked'
  | 'rejected'
  | 'newToFilePct'
  | 'price'
  | 'costOfTotal'
  | 'costOfUnique';

function linkAccessor(row: CtoLinkRow, key: LinkSortKey): string | number | null {
  switch (key) {
    case 'campaignName':
      return row.campaignName;
    case 'promoTypeName':
      return row.promoTypeName;
    case 'linkStatus':
      return row.linkStatus;
    case 'leads':
      return row.leads;
    case 'uniqueLeads':
      return row.uniqueLeads;
    case 'success':
      return row.success;
    case 'duplicate':
      return row.duplicate;
    case 'unchecked':
      return row.unchecked;
    case 'rejected':
      return row.rejected;
    case 'newToFilePct':
      return row.newToFilePct;
    case 'price':
      return row.price;
    case 'costOfTotal':
      return row.costOfTotal;
    case 'costOfUnique':
      return row.costOfUnique;
  }
}

/** The three per-row export buttons — `window.open`s the scoped `cto-leads.csv`. */
function ExportButtons({
  idSuffix,
  onExport,
}: {
  idSuffix: string;
  onExport: (variant: CtoLeadsVariant) => void;
}) {
  const labels: readonly [CtoLeadsVariant, string][] = [
    ['unique', 'Unique'],
    ['all', 'All'],
    ['rejected', 'Rejected'],
  ];
  return (
    <div className="flex justify-end gap-1">
      {labels.map(([variant, label]) => (
        <button
          key={variant}
          type="button"
          data-testid={`cto-export-${variant}-${idSuffix}`}
          onClick={() => onExport(variant)}
          className="rounded-md px-1.5 py-0.5 text-xs font-medium text-accent hover:underline"
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** A source matches by its Traffic Source label or Site ID. */
function matchesSource(q: string, source: CtoSourceRow): boolean {
  return matchesSearch(q, source.label, source.siteCode);
}

/** A link matches by its campaign or promo type name. */
function matchesLink(q: string, link: CtoLinkRow): boolean {
  return matchesSearch(q, link.campaignName, link.promoTypeName);
}

const LEVEL1_COLSPAN = 13;
const LEVEL2_COLSPAN = 14;

export function CtoStats() {
  const navigate = useNavigate();
  const range = useReportRange();
  const { from, to } = range;
  const [campaignId, setCampaignId] = useState('');
  // Test leads are shared across the style tabs (URL-backed, 087-01); CTO has no status filter.
  const { includeTest, setIncludeTest } = useSharedReportFilters();
  const [search, setSearch] = useState('');
  const [campaigns, setCampaigns] = useState<CampaignDetailResponse[]>([]);
  const [data, setData] = useState<CtoStatsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);

  // Dropdown loader — the shared typed client (best-effort: an empty dropdown
  // list degrades the filter, not the report).
  useEffect(() => {
    api
      .getCampaigns()
      .then(setCampaigns)
      .catch(() => {
        // Leave the previous (empty) list on error.
      });
  }, []);

  // Fetch on every filter change; the stale response of a superseded filter
  // set is dropped rather than rendered (co-op-counts.tsx's idiom).
  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCtoStats({ from, to, includeTest, campaignId })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, includeTest, campaignId]);

  // A filter change can drop the drilled-into source from the response (or
  // fail the fetch entirely) — fall back to level 1 rather than show a level
  // 2 for a source no longer in range.
  useEffect(() => {
    if (selectedSiteId && data && !data.sources.some((s) => s.siteId === selectedSiteId)) {
      setSelectedSiteId(null);
    }
  }, [data, selectedSiteId]);

  const ctoCampaignOptions = useMemo(
    () => campaigns.filter((c) => c.campaignStyle === 'cto_email'),
    [campaigns]
  );

  // The search narrows the rows only — the footers keep the server's totals (087-01 D5).
  const searchActive = search.trim() !== '';

  // Level 1: a source shows when it matches itself or any of its links does.
  const searchedSources = useMemo(
    () =>
      (data?.sources ?? []).filter(
        (source) =>
          matchesSource(search, source) ||
          (data?.links ?? []).some(
            (link) => link.siteId === source.siteId && matchesLink(search, link)
          )
      ),
    [data, search]
  );

  const {
    sortedRows: sortedSources,
    sort: sourceSort,
    toggleSort: toggleSourceSort,
  } = useClientSort<CtoSourceRow, SourceSortKey>(searchedSources, sourceAccessor);

  const selectedSource = useMemo(
    () =>
      selectedSiteId ? (data?.sources.find((s) => s.siteId === selectedSiteId) ?? null) : null,
    [data, selectedSiteId]
  );

  const allLinksForSource = useMemo(
    () => (selectedSiteId ? (data?.links.filter((l) => l.siteId === selectedSiteId) ?? []) : []),
    [data, selectedSiteId]
  );

  // Level 2: a source that matched itself shows all its links, else only the matching ones.
  const linksForSource = useMemo(
    () =>
      selectedSource === null || matchesSource(search, selectedSource)
        ? allLinksForSource
        : allLinksForSource.filter((link) => matchesLink(search, link)),
    [allLinksForSource, selectedSource, search]
  );

  const {
    sortedRows: sortedLinks,
    sort: linkSort,
    toggleSort: toggleLinkSort,
  } = useClientSort<CtoLinkRow, LinkSortKey>(linksForSource, linkAccessor);

  const exportSource = (source: CtoSourceRow) => (variant: CtoLeadsVariant) => {
    window.open(
      ctoLeadsExportUrl({
        from,
        to,
        includeTest,
        siteId: source.siteId,
        campaignId: campaignId || undefined,
        variant,
      })
    );
  };

  const exportLink = (link: CtoLinkRow) => (variant: CtoLeadsVariant) => {
    window.open(
      ctoLeadsExportUrl({
        from,
        to,
        includeTest,
        siteId: link.siteId,
        linkId: link.linkId,
        campaignId: campaignId || undefined,
        variant,
      })
    );
  };

  return (
    <ReportShell
      title="CTO Email"
      desc="Leads by traffic source — unique across a source's links; cost per link rate, and a source with mixed or unpriced links shows the per-link sum. Test leads hidden by default."
    >
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <RangeFilters
            preset={range.preset}
            from={range.from}
            to={range.to}
            applyPreset={range.applyPreset}
            setRange={range.setRange}
            testIdPrefix="cto"
          />

          <Select
            aria-label="Campaign"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className="h-9 min-w-[180px]"
          >
            <option value="">All campaigns</option>
            {ctoCampaignOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>

          <TestLeadsToggle checked={includeTest} onChange={setIncludeTest} />

          <ReportSearch
            value={search}
            onChange={setSearch}
            placeholder="Search source, campaign or promo"
            testId="cto-search"
          />

          {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>

        <SearchNote
          active={searchActive}
          shown={selectedSource ? sortedLinks.length : sortedSources.length}
          total={selectedSource ? allLinksForSource.length : (data?.sources.length ?? 0)}
          testId="cto-search-note"
        />

        {error && <ErrorBanner message={error} />}

        {selectedSiteId !== null && selectedSource && (
          <div className="flex items-center gap-3 border-b border-line px-4 py-3">
            <button
              type="button"
              data-testid="cto-back"
              onClick={() => setSelectedSiteId(null)}
              className="text-sm font-medium text-accent hover:underline"
            >
              ← All sources
            </button>
            <h2 className="text-sm font-semibold text-ink">{selectedSource.label}</h2>
          </div>
        )}

        <div className="overflow-x-auto">
          {selectedSiteId === null || !selectedSource ? (
            <Table data-testid="cto-table">
              <TableHeader>
                <TableRow>
                  <SortableHeader
                    label="Site ID"
                    sortKey="siteCode"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Traffic source"
                    sortKey="label"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Total leads"
                    sortKey="leads"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Unique"
                    sortKey="uniqueLeads"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                    title={UNIQUE_COLUMN_HELP}
                  />
                  <SortableHeader
                    label="Success"
                    sortKey="success"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Duplicate"
                    sortKey="duplicate"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Not checked"
                    sortKey="unchecked"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                    title={NOT_CHECKED_COLUMN_HELP}
                  />
                  <SortableHeader
                    label="Rejected"
                    sortKey="rejected"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="New to File"
                    sortKey="newToFilePct"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Rate"
                    sortKey="rate"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Cost of total leads"
                    sortKey="costOfTotal"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <SortableHeader
                    label="Cost of unique leads"
                    sortKey="costOfUnique"
                    sort={sourceSort}
                    onToggle={toggleSourceSort}
                  />
                  <TableHead className="text-right">Exports</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data === null ? (
                  <LoadingRow colSpan={LEVEL1_COLSPAN} />
                ) : sortedSources.length === 0 ? (
                  <EmptyRow
                    colSpan={LEVEL1_COLSPAN}
                    message={
                      data.sources.length > 0 && searchActive
                        ? `No rows match "${search.trim()}".`
                        : 'No leads in this range.'
                    }
                  />
                ) : (
                  sortedSources.map((source) => {
                    const rate = rateCell(
                      source.rates,
                      source.hasUnpricedLink,
                      source.costOfUniqueMixed
                    );
                    return (
                      <TableRow key={source.siteId} data-testid={`cto-row-${source.siteId}`}>
                        <TableCell>{source.siteCode}</TableCell>
                        <TableCell>
                          <button
                            type="button"
                            onClick={() => setSelectedSiteId(source.siteId)}
                            className="text-accent hover:underline"
                          >
                            {source.label}
                          </button>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.leads)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.uniqueLeads)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.success)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.duplicate)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.unchecked)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(source.rejected)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtNewToFile(source.newToFilePct)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums" title={rate.title}>
                          {rate.text}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(source.costOfTotal)}
                        </TableCell>
                        <TableCell
                          className="text-right tabular-nums"
                          title={source.costOfUniqueMixed ? MIXED_COST_TITLE : undefined}
                        >
                          {fmtMoney(source.costOfUnique)}
                        </TableCell>
                        <TableCell className="text-right">
                          <ExportButtons idSuffix={source.siteId} onExport={exportSource(source)} />
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
              {data !== null && data.sources.length > 0 && (
                <TableFooter>
                  <TableRow data-testid="cto-totals-row">
                    <TableCell>Total</TableCell>
                    <TableCell />
                    <TableCell className="text-right tabular-nums">
                      {cell(data.totals.leads)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(data.totals.uniqueLeads)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(data.totals.success)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(data.totals.duplicate)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                      {cell(data.totals.unchecked)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {cell(data.totals.rejected)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {fmtNewToFile(data.totals.newToFilePct)}
                    </TableCell>
                    <TableCell />
                    <TableCell className="text-right tabular-nums">
                      {fmtMoney(data.totals.costOfTotal)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums" title={LEVEL1_TOTALS_COST_TITLE}>
                      {fmtMoney(data.totals.costOfUnique)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          ) : (
            <Table data-testid="cto-table">
              <TableHeader>
                <TableRow>
                  <SortableHeader
                    label="Campaign"
                    sortKey="campaignName"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                  />
                  <SortableHeader
                    label="Promo"
                    sortKey="promoTypeName"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                  />
                  <SortableHeader
                    label="Status"
                    sortKey="linkStatus"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                  />
                  <SortableHeader
                    label="Total leads"
                    sortKey="leads"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                  />
                  <SortableHeader
                    label="Unique"
                    sortKey="uniqueLeads"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                    title={UNIQUE_TOTAL_TITLE}
                  />
                  <SortableHeader
                    label="Success"
                    sortKey="success"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                    title={UNIQUE_TOTAL_TITLE}
                  />
                  <SortableHeader
                    label="Duplicate"
                    sortKey="duplicate"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                    title={UNIQUE_TOTAL_TITLE}
                  />
                  <SortableHeader
                    label="Not checked"
                    sortKey="unchecked"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                    title={UNIQUE_TOTAL_TITLE}
                  />
                  <SortableHeader
                    label="Rejected"
                    sortKey="rejected"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                  />
                  <SortableHeader
                    label="New to File"
                    sortKey="newToFilePct"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                  />
                  <SortableHeader
                    label="Rate"
                    sortKey="price"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                  />
                  <SortableHeader
                    label="Cost of total leads"
                    sortKey="costOfTotal"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                  />
                  <SortableHeader
                    label="Cost of unique leads"
                    sortKey="costOfUnique"
                    sort={linkSort}
                    onToggle={toggleLinkSort}
                    title={LEVEL2_COST_TITLE}
                  />
                  <TableHead className="text-right">Exports</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedLinks.length === 0 ? (
                  <EmptyRow
                    colSpan={LEVEL2_COLSPAN}
                    message={
                      allLinksForSource.length > 0 && searchActive
                        ? `No rows match "${search.trim()}".`
                        : 'No leads in this range.'
                    }
                  />
                ) : (
                  sortedLinks.map((link) => (
                    <TableRow key={link.linkId} data-testid={`cto-link-row-${link.linkId}`}>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => navigate(`/campaigns/${link.campaignId}`)}
                          className="text-accent hover:underline"
                        >
                          {link.campaignName}
                        </button>
                      </TableCell>
                      <TableCell>{link.promoTypeName}</TableCell>
                      <TableCell>{link.linkStatus}</TableCell>
                      <TableCell className="text-right tabular-nums">{cell(link.leads)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(link.uniqueLeads)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(link.success)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(link.duplicate)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(link.unchecked)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {cell(link.rejected)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtNewToFile(link.newToFilePct)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtMoney(link.price)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtMoney(link.costOfTotal)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {fmtMoney(link.costOfUnique)}
                      </TableCell>
                      <TableCell className="text-right">
                        <ExportButtons idSuffix={link.linkId} onExport={exportLink(link)} />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              {allLinksForSource.length > 0 &&
                (() => {
                  const rate = rateCell(
                    selectedSource.rates,
                    selectedSource.hasUnpricedLink,
                    selectedSource.costOfUniqueMixed
                  );
                  return (
                    <TableFooter>
                      <TableRow data-testid="cto-totals-row">
                        <TableCell>Total</TableCell>
                        <TableCell />
                        <TableCell />
                        <TableCell className="text-right tabular-nums">
                          {cell(selectedSource.leads)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                          {cell(selectedSource.uniqueLeads)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                          {cell(selectedSource.success)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                          {cell(selectedSource.duplicate)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums" title={UNIQUE_TOTAL_TITLE}>
                          {cell(selectedSource.unchecked)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {cell(selectedSource.rejected)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtNewToFile(selectedSource.newToFilePct)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums" title={rate.title}>
                          {rate.text}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtMoney(selectedSource.costOfTotal)}
                        </TableCell>
                        <TableCell className="text-right tabular-nums" title={LEVEL2_COST_TITLE}>
                          {fmtMoney(selectedSource.costOfUnique)}
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableFooter>
                  );
                })()}
            </Table>
          )}
        </div>
      </Card>
    </ReportShell>
  );
}
